import type { Copy } from "../types";

export const en: Copy = {
    seo: {
      title: "Car Age Calculator | Vehicle Age Checker by Year and Month",
      description:
        "Check a vehicle's age from manufacturing year and optional month. See years, months, days, total days, and milestone timing in one clean calculator.",
      keywords: [
        "car age calculator",
        "vehicle age calculator",
        "car manufacturing year calculator",
        "vehicle manufacturing month calculator",
        "how old is my car",
        "car age by year",
        "vehicle age checker",
        "RC age calculator",
        "car age by month",
        "vehicle date calculator",
        "mParivahan alternative",
        "Parivahan alternative",
        "RTO vehicle age calculator",
        "car registration age checker",
        "vehicle registration age",
      ],
      faq: [
        {
          question: "What does this calculator do?",
          answer:
            "It calculates the exact age of a vehicle from the selected manufacturing year and optional month, then shows years, months, days, and milestone information.",
        },
        {
          question: "Why is there an optional month?",
          answer:
            "The month setting gives a more precise age calculation. If you skip it, the app uses January of the selected year.",
        },
        {
          question: "Can I select a future year or month?",
          answer:
            "No. Future years are blocked, and future months are disabled when the selected year is the current year.",
        },
      ],
    },
    headerTagline: "Vehicle utility",
    headerTitle: "Car Age Calculator",
    reset: "Reset",
    switchToLight: "Switch to light mode",
    switchToDark: "Switch to dark mode",
    languageLabel: "Language",
    languageEnglish: "English",
    languageHindi: "Hindi",
    heroEyebrow: "Car age, clearly",
    heroTitle: "A clean date tool for vehicle age.",
    heroDescription:
      "Select the manufacturing year. Add a month only if you want a more precise age result.",
    yearSelectLabel: "Manufacturing year",
    monthSelectLabel: "Manufacturing month",
    includeMonthLabel: "Include specific month for more precision",
    includeMonthNote:
      "If you do not include a month, January is used for the calculation.",
    yearOnlyBadge: "Year only",
    yearMonthBadge: "Year + month",
    yearPlaceholder: "Select year",
    monthPlaceholder: "Select month",
    inputTitle: "Manufacturing year",
    inputLabel: "Manufacturing year",
    blockedDateNote: "Future years are blocked.",
    result: "Result",
    vehicleAgeLead: "Your vehicle is",
    asOfToday: "As of today",
    years: "Years",
    months: "Months",
    days: "Days",
    totalDays: (count) => `${count.toLocaleString("en-IN")} total days old`,
    ageLabels: {
      almostNew: "Almost new",
      modern: "Modern",
      mature: "Mature",
      classic: "Classic",
    },
    estimatedValue: {
      high: "High resale potential",
      healthy: "Healthy value retention",
      moderate: "Moderate depreciation",
      collector: "Collector interest may grow",
    },
    maintenanceTitle: "Maintenance",
    maintenance: {
      routine: "Routine service and OEM upkeep",
      watch: "Watch brakes, fluids, and tires",
      plan: "Plan for wear items and inspections",
      specialized: "Specialized maintenance becomes valuable",
    },
    registrationTitle: "Registration",
    registration: {
      collector: "May qualify for collector-style treatment in some regions",
      classic: "Classic-car style registration may apply in some regions",
      standard: "Expect standard renewals and regional checks",
      regular: "Regular registration usually applies",
    },
    upcomingMilestoneTitle: "Upcoming milestone",
    milestoneTimelineTitle: "Milestone timeline",
    milestoneRemaining: ({ label, days }) => `${label} in ${days.toLocaleString("en-IN")} days`,
    reached: "Reached",
    upcoming: "Upcoming",
    allMilestonesReachedTitle: "All listed milestones reached",
    allMilestonesReachedBody:
      "This vehicle has passed the milestone range currently shown in the app.",
    copySummary: "Copy summary",
    shareResult: "Share result",
    resetMessage: "Calculator reset",
    summaryCopied: "Summary copied to clipboard",
    clipboardFailed: "Clipboard access failed",
    emptyTitle: "Choose a manufacturing year to get the full breakdown",
    emptyDescription:
      "You’ll see exact age, total days, maintenance notes, classic status, and milestone timing once a year is selected.",
    shareTitle: "Car Age Calculator",
    summary: ({ label, years, months, days, asOf }) =>
      `My car was manufactured in ${label} and is ${years} years, ${months} months, and ${days} days old as of ${asOf}.`,
    milestones: {
      1: { label: "First year", note: "Useful for warranty and service planning." },
      3: { label: "Three years", note: "A common point for resale and value shifts." },
      5: { label: "Five years", note: "Good time for deeper maintenance budgeting." },
      10: { label: "Ten years", note: "Aging components deserve extra attention." },
      15: { label: "Fifteen years", note: "Many regions start classic-style treatment here." },
      25: { label: "Twenty-five years", note: "Collector and import-friendly in some markets." },
    },
  };
